<template>
  <a-modal
    v-model:visible="visible"
    title="数据库"
    :width="1050"
    :after-close="afterClose"
  >
    <div
      tabindex="-1"
      @keydown.up.stop="() => selectOffset(-1)"
      @keydown.down.stop="() => selectOffset(1)"
      @keydown.enter="() => !composition && add()"
    >
      <!-- search -->
      <div class="">
        <div
          class="search w-full my-0 px-2.5 py-3 bg-[#2C2C2C] inline-flex items-center rounded-lg"
        >
          <Icon
            :width="20"
            :height="20"
            class="mr-2"
            icon="search.svg"
            fill="transparent"
          />
          <a-input
            ref="searchInput"
            v-model:value="search"
            :bordered="false"
            class="font-bold p-0"
            placeholder="关键字搜索"
            @compositionstart="() => (composition = true)"
            @compositionend="() => (composition = false)"
          >
            <template #suffix>
              <Icon
                v-if="search.length"
                :width="14"
                :height="14"
                class="mr-2"
                icon="add3.svg"
                fill="transparent"
                @click="
                  () => {
                    search = '';
                  }
                "
              />
            </template>
          </a-input>
        </div>
      </div>
      <div v-if="Object.keys(dataList).length">
        <div class="w-full h-[40px] _grid mt-3 px-4 items-center opacity-70">
          <span class="text-center text-base font-thin">数据</span>
          <span class="text-center text-base font-thin">描述</span>
          <span class="text-center text-base font-thin">返回值</span>
        </div>
        <div class="h-[450px] overflow-y-auto">
          <div
            v-for="(list, label) in dataList"
            :key="label"
            class="text-xs text-[#CECECE]"
          >
            <div class="text-sm py-2 opacity-[0.6] px-[18px]">
              <span>{{ label }}</span>
            </div>
            <div
              v-for="item of list"
              :key="item.key"
              ref="listItems"
              :data-selected="item.key == selectedSource?.key ? '1' : '0'"
              class="_grid text-base text-[#FFF]/[0.7] py-2 min-h-10 px-4 border-[2px] border-[transparent] hover:border-[#0D84F2] items-center rounded-lg"
              :class="{
                'bg-[#4A5878]': item.key == selectedSource?.key,
                'opacity-30': disabled(item)
              }"
              tabindex="-1"
              @click="setSelectSource(item)"
              @dblclick="() => selectThenAdd(item)"
            >
              <!-- 名称 -->
              <span class="text-base font-medium">
                <EditableTitle
                  disabled
                  tree-id=""
                  :title="item.label"
                  :highlight-text="search"
                />
              </span>
              <!-- 介绍 -->
              <span class="text-sm">{{ item.comment }}</span>
              <!-- 返回值 -->
              <span class="text-sm"
                >{{
                  item.returnValue ??
                  (item.minValue !== undefined
                    ? `[${item.minValue},` + ' ' + `${item.maxValue}]`
                    : item.comment)
                }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div
        v-else
        class="text-center h-[502px] flex items-center justify-center"
      >
        暂无相关数据源
      </div>
    </div>

    <template #footer>
      <a-button key="back" @click="() => (visible = false)">关闭</a-button>
      <a-button
        :disabled="!selectedSource?.key"
        type="primary"
        @click="() => add()"
      >
        添加
      </a-button>
    </template>
  </a-modal>
</template>

<script lang="ts" setup>
import { dataSourceList } from '@/resources/data-source';
import { useDataSource } from '@/hooks';
import { Modal } from 'ant-design-vue';
import { PropType, computed, nextTick, ref, watch } from 'vue';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import {
  canUse,
  canUseDataSource,
  canUseDataSourceByShape
} from '@/utils/can-use.utils';
import { useEditorStore } from '@/store/use-editor-store';
import { ElectronApi } from '@/services/electron.api';
import { DataSourceFormat } from '@/resources/enum';

const props = defineProps({
  // 已选择的sourceKey
  sourceKey: {
    type: String,
    default: undefined
  },
  shapeTypes: {
    type: Array as PropType<DataSourceItem['allowShapeTypes']>,
    default: undefined
  },
  disableString: {
    type: Boolean,
    default: false
  },
  sourceTags: {
    type: Array as PropType<DataSourceItem['tag'][]>,
    default: () => {
      return ['normal', 'compound'];
    }
  },
  hiddenFakeItem: {
    type: Boolean,
    default: false
  },
  showHiddenItem: {
    type: Boolean,
    default: false
  },
  changeTimeComment: {
    type: Boolean,
    default: false
  }
});

const emits = defineEmits<{
  (event: 'select', dataSource: DataSourceItem): void;
  (event: 'closed'): void;
}>();

//
const watchfaceStore = useWatchfaceStore();
const editorStore = useEditorStore();

//格式化数据列表
const search = ref('');
const dataList = computed(() => {
  const deviceType = (watchfaceStore.deviceType ?? 'N62') as 'N62';
  const res: Record<string, DataSourceItem[]> = {};
  const supportText = canUse('shape-text').valid;
  Object.entries(dataSourceList).forEach((dataSource) => {
    const [group, items] = dataSource;
    const items2 = items
      .filter((item) => (props.showHiddenItem ? true : !item.hidden))
      .filter((item) => {
        if (!props.hiddenFakeItem) return true;
        // 日出日落时间保留，不被 hiddenFakeItem 过滤
        if (['weatherCurrentSunRise', 'weatherCurrentSunSet'].includes(item.key)) return true;
        return (
          !item.widgetImageArray && !['timeChineseTime'].includes(item.key)
        );
      })
      .filter((item) => props.sourceTags.includes(item.tag ?? 'normal'))
      .filter((item) => {
        return canUseDataSource(item, deviceType);
      })
      .filter((item) => {
        if (item.key.toLocaleLowerCase().includes('string') && !supportText) {
          return false;
        } else {
          return true;
        }
      })
      .filter(
        (item) =>
          !search.value ||
          item.label.toLowerCase().includes(search.value.trim().toLowerCase())
      )
      .filter((item) =>
        item.onlyUseInPro ? ElectronApi.isProfessional() : true
      );
    // 处理温度类数据源华氏度问题
    items2.forEach((item) => {
      if (!canUse('temperature-fahrenheit').valid) {
        if (item.key.endsWith('_temp')) item.comment = '单位°';
        if (item.key == 'weatherCurrentTemperature') {
          item.label = '温度';
          item.comment = '单位°';
        }
      }
      item.comment = item.comment?.replace('，该数据已经拼接分钟','').replace('，该数据已经拼接秒钟','');
      if (['time','dualtime'].includes(item.key)) {
        let isProfessional = !(ElectronApi.isNormal() || editorStore.theme?.type === 'AOD');
        item.returnValue = isProfessional?'[00:00:00, 24:00:00)':'[00:00, 24:00)';
        item.formats = isProfessional ?
          [DataSourceFormat['Format_8:00:00'],DataSourceFormat['Format_08:00:00']] : 
          [DataSourceFormat['Format_8:00'],DataSourceFormat['Format_08:00']];
        if (item.key === 'dualtime') {
          item.comment =  isProfessional ? '目标时区的时分秒' : '目标时区的时分';
        }
        if (item.key === 'time') {
          item.comment = isProfessional ? '时,分和秒（跟随系统24H/12H设置）' : '时和分（跟随系统24H/12H设置）'
        }
      }
      if (props.changeTimeComment) { // 小时，分钟参数的comment增加提示语句
        if (
          [
            'timeHour',
            'timeHour24H',
            'timeHour12H',
            'dualtimeHour'
          ].includes(item.key)
        ) {
          item.comment = item.comment + '，该数据已经拼接分钟';
        }
        if (['timeMinute', 'dualtimeMinute'].includes(item.key)) {
          item.comment = item.comment + '，该数据已经拼接秒钟';
        }
      }
    });
    if (items2.length) {
      res[group] = items2;
    }
  });
  return res;
});

const disabled = (item: DataSourceItem) => {
  const shapeTypes = props.shapeTypes;
  const isString =
    item.key.toLowerCase().includes('string') ||
    item.key == 'systemSensorCompassDirection';
  if (isString && props.disableString) return true;

  if (!shapeTypes) return false;

  const isAllowed = shapeTypes.some((type) =>
    canUseDataSourceByShape({
      sourceKey: item.key,
      shapeType: type as 'image',
      deviceType: watchfaceStore.deviceType,
      themeType: editorStore.theme?.type ?? 'normal'
    })
  );
  return !isAllowed;
};
const selectedSource = ref<DataSourceItem>();
const setSelectSource = (item: DataSourceItem) => {
  selectedSource.value = item;
};

// 搜索时默认选中第一项
watch(dataList, (newDataList: Record<string, DataSourceItem[]>) => {
  let res = Object.keys(newDataList).reduce(
    (acc: DataSourceItem[], cur: string) => {
      return [...acc, ...dataList.value[cur]] as DataSourceItem[];
    },
    []
  );
  if (res.length) selectedSource.value = res[0];
});

const listItems = ref();

const scrollToSelected = () =>
  listItems?.value
    .find((i: HTMLElement) => (i.getAttribute('data-selected') ?? 0) === '1')
    ?.scrollIntoView({
      block: 'center'
    });

const selectOffset = (offset: number) => {
  const flattened = Object.keys(dataList.value).reduce(
    (acc: DataSourceItem[], cur: string) => {
      return [...acc, ...dataList.value[cur]] as DataSourceItem[];
    },
    []
  );
  const cur = flattened.findIndex(
    (i) => i.key === (selectedSource.value as DataSourceItem).key
  );
  const index = Math.min(Math.max(0, cur + offset), flattened.length - 1);
  if (index === cur) return;
  setSelectSource(flattened[index]);
  scrollToSelected();
};

const { findOne } = useDataSource();

const afterClose = () => {
  selectedSource.value = undefined;
  search.value = '';
  emits('closed');
};

const selectThenAdd = (item: DataSourceItem) => {
  setSelectSource(item);
  add();
};

const add = () => {
  if (!selectedSource.value) return;
  if (disabled(selectedSource.value)) {
    Modal.warning({
      title: '提示',
      content: '当前元素暂不支持添加此数据源。',
      okText: '知道了'
    });
    return;
  }
  emits('select', { ...selectedSource.value });
  visible.value = false;
};

const visible = ref(false);
const searchInput = ref();
defineExpose({
  open: async () => {
    visible.value = true;
    const sourceItem = findOne(props.sourceKey ?? '');
    if (sourceItem) selectedSource.value = sourceItem;

    await nextTick();
    searchInput.value?.focus?.();
    scrollToSelected();
  }
});

// CJK输入法composition状态时 不应处理回车事件
const composition = ref(false);
</script>
<style scoped lang="less">
._grid {
  display: grid;
  grid-template-columns: 200px 1fr 1fr;
  column-gap: 20px;
}
.search {
  :deep(.ant-input) {
    font-size: 16px;
    line-height: 20px !important;
    font-weight: 400;
    height: 20px;
  }
  .ant-input-affix-wrapper:before {
    display: none;
  }
}
</style>
